// This will be populated from the backend
export let BOAT_TYPES = {};

/** Codes from backend Boat enum — used when BOAT_TYPES is empty or navigation hasn’t awaited loadBoatTypes() */
const KNOWN_BOAT_CODES = [
  '8+',
  '4-',
  '4x',
  '4+',
  '4*',
  '2-',
  '2x',
  '2+',
  '2*',
  '1x',
];

/**
 * Mirrors backend `/boat-types` derivation: seats from first digit, cox only if "+" in code,
 * strict sides for sweep boats.
 */
function buildFallbackLayout(code) {
  const hasCox = String(code).includes('+');
  const first = String(code)[0];
  const seats = first && /\d/.test(first) ? parseInt(first, 10) : 1;
  const sidePreferenceStrict = ['8+', '4-', '2-', '4+', '2+'].includes(code);
  return {
    seats,
    hasCox,
    has_cox: hasCox,
    sidePreferenceStrict,
    side_preference_strict: sidePreferenceStrict,
    category: sidePreferenceStrict ? 'sweep' : 'sculling',
    name: code,
  };
}

/**
 * Resolved layout for UI (CrewLineupEditor, autoplanner). Prefer API-loaded BOAT_TYPES; otherwise
 * use the same rules as the server so "2x" / "4-" never show as unknown before fetch completes.
 */
export function getBoatTypeLayout(boatType) {
  const key = boatType != null ? String(boatType).trim() : '';
  if (!key) return buildFallbackLayout('8+');
  if (BOAT_TYPES[key]) return BOAT_TYPES[key];
  if (KNOWN_BOAT_CODES.includes(key)) return buildFallbackLayout(key);
  return buildFallbackLayout('1x');
}

// Function to load boat types from backend
export const loadBoatTypes = async () => {
  try {
    const { boatTypesAPI } = await import('../services/api');
    const data = await boatTypesAPI.getBoatTypes();

    // Transform snake_case properties to camelCase for frontend consistency
    const transformedBoatTypes = {};
    Object.keys(data.boat_types).forEach((boatTypeKey) => {
      const boatType = data.boat_types[boatTypeKey];
      transformedBoatTypes[boatTypeKey] = {
        ...boatType,
        hasCox: boatType.has_cox, // Convert has_cox to hasCox
        sidePreferenceStrict: boatType.side_preference_strict, // Convert side_preference_strict to sidePreferenceStrict
      };
    });
    BOAT_TYPES = transformedBoatTypes;

    // Return the transformed data structure
    return {
      ...data,
      boat_types: transformedBoatTypes,
    };
  } catch (error) {
    console.error('Failed to load boat types:', error);
  }
  return null;
};

// Function to get side preference for a boat type
export const getSidePreferenceForBoatType = (boatType) => {
  if (!BOAT_TYPES[boatType]) return null;

  return {
    isStrict: BOAT_TYPES[boatType].sidePreferenceStrict,
    category: BOAT_TYPES[boatType].category,
    description: BOAT_TYPES[boatType].sidePreferenceStrict
      ? 'Sweep boats require specific side assignments'
      : 'Sculling boats allow flexible side assignments',
  };
};

export const getCrewCompletionStatus = (crew) => {
  if (!crew || !crew.members) return 'empty';

  const boatType = getBoatTypeLayout(crew.boat_type);

  const filledPositions = crew.members.length;
  const totalPositions = boatType.seats + (boatType.hasCox ? 1 : 0); // Add 1 for cox if applicable

  // No crew members
  if (filledPositions === 0) return 'empty';

  // Complete when all positions filled (or more for experimental crews)
  if (crew.is_experimental ? filledPositions >= totalPositions : filledPositions === totalPositions)
    return 'complete';

  // Some positions filled
  if (filledPositions > 0 && filledPositions < totalPositions) return 'partial';

  return 'unknown';
};
